import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { listPipelineSchedule, setPipelineRetired, setPipelineScheduleEnabled, type AdminPipelinesApiError, type PipelineSchedule } from '@/api/adminPipelines';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import styles from '@/features/admin/PipelineRunsPage.module.css';

function cadence(p: PipelineSchedule): string {
  if (p.daily_at_utc) return `daily at ${p.daily_at_utc} UTC`;
  if (p.interval_sec) return p.interval_sec % 3600 === 0 ? `every ${p.interval_sec / 3600}h` : `every ${Math.round(p.interval_sec / 60)}m`;
  return '—';
}

/*
 * Automatic-run switches for the selected backend. The scheduler consults the
 * flag on every tick, so a toggle takes effect within one interval — manual
 * "Run now" is never gated by it.
 */
export function AutoRunPanel({ baseUrl }: { baseUrl: string }) {
  const queryClient = useQueryClient();
  const scheduleKey = ['admin', 'pipeline-schedule', baseUrl] as const;
  const [showRetired, setShowRetired] = useState(false);

  const scheduleQuery = useQuery({
    queryKey: scheduleKey,
    queryFn: () => listPipelineSchedule(baseUrl),
    enabled: baseUrl.length > 0,
  });

  const toggle = useMutation({
    mutationFn: (p: PipelineSchedule) => setPipelineScheduleEnabled(baseUrl, p.pipeline_name, !p.enabled),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scheduleKey }),
  });

  const retire = useMutation({
    mutationFn: (p: PipelineSchedule) => setPipelineRetired(baseUrl, p.pipeline_name, !p.retired),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: scheduleKey }),
  });

  if (scheduleQuery.isLoading) return <div className={styles.stepsState}>Loading schedule…</div>;
  if (scheduleQuery.isError) {
    return <div className={styles.stepsState}>Could not load schedule: {(scheduleQuery.error as AdminPipelinesApiError)?.message ?? 'unknown error'}</div>;
  }

  const { pipelines, schedulerEnabled } = scheduleQuery.data!;
  const active = pipelines.filter((p) => !p.retired);
  const retired = pipelines.filter((p) => p.retired);

  return (
    <div className={styles.autoRun}>
      <div className={styles.autoRunHead}>
        Automatic runs
        {!schedulerEnabled && (
          <span className={styles.autoRunOff}> — scheduler is OFF on this backend (env), toggles below have no effect until it is enabled</span>
        )}
      </div>
      {toggle.isError && <Alert variant="error">{(toggle.error as AdminPipelinesApiError)?.message ?? 'Could not update the schedule'}</Alert>}
      {retire.isError && <Alert variant="error">{(retire.error as AdminPipelinesApiError)?.message ?? 'Could not update the pipeline'}</Alert>}
      {active.length === 0 && <div className={styles.muted}>No active pipelines — all retired.</div>}
      {active.map((p) => (
        <div key={p.pipeline_name} className={styles.autoRunRow}>
          <div className={styles.autoRunName}>{p.pipeline_name}</div>
          <div className={styles.muted}>{cadence(p)}</div>
          <Badge variant={p.enabled ? 'active' : 'inactive'}>{p.enabled ? 'auto' : 'paused'}</Badge>
          <div className={styles.autoRunActions}>
            <Button variant="ghost" disabled={toggle.isPending} onClick={() => toggle.mutate(p)}>
              {p.enabled ? 'Disable' : 'Enable'}
            </Button>
            <Button variant="ghost" disabled={retire.isPending} title="Never auto-run again and hide from this list" onClick={() => retire.mutate(p)}>
              Retire
            </Button>
          </div>
        </div>
      ))}
      {retired.length > 0 && (
        <div className={styles.retiredFoot}>
          <Button variant="link" onClick={() => setShowRetired((v) => !v)}>
            {showRetired ? 'Hide retired' : `Show retired (${retired.length})`}
          </Button>
          {showRetired &&
            retired.map((p) => (
              <div key={p.pipeline_name} className={styles.autoRunRow}>
                <div className={styles.autoRunName}>{p.pipeline_name}</div>
                <div className={styles.muted}>{cadence(p)}</div>
                <Badge variant="client">retired</Badge>
                <div className={styles.autoRunActions}>
                  <Button variant="ghost" disabled={retire.isPending} onClick={() => retire.mutate(p)}>
                    Restore
                  </Button>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
